<template>
    <section class="flight-booking-section">
      <div class="content-panel">
        <div class="panel-title">
          <h3>{{ $t('billingInformation') }}</h3>
        </div>

        <div class="two-col-form">
          <div class="panel-body">
            <div class="two-col-form">
              <div class="form-group">
                <label for="firstName">{{ $t('firstName') }}</label>
                <input v-validate="'required'" id="firstName" v-model="customer.firstName" type="text" :name="$t('firstName')" />
                <span class="invalid">{{ errors.first($t('firstName')) }}</span>
              </div>

              <div class="form-group">
                <label for="lastName">{{ $t('lastName') }}</label>
                <input v-validate="'required'" id="lastName" v-model="customer.lastName" type="text" :name="$t('lastName')" />
                <span class="invalid">{{ errors.first($t('lastName')) }}</span>
              </div>

              <div class="form-group">
                <label for="emailAddress">{{ $t('emailAddress') }}</label>
                <input v-validate="'required'" id="emailAddress" v-model="customer.email" type="email" :name="$t('emailAddress')" />
                <span class="invalid">{{ errors.first($t('emailAddress')) }}</span>
              </div>

              <div class="form-group">
                <label for="nationality">{{ $t('nationality') }}</label>
                <v-select v-validate="'required'" id="nationality" v-model="customer.nationality" :name="$t('nationality')" :options="getCountryList" :reduce="country => country.code" label="name">
                </v-select>
                <span class="invalid">{{ errors.first($t('nationality')) }}</span>
              </div>

              <div class="form-group">
                <label for="phoneNumber">{{ $t('phoneNumber') }}</label>
                <div class="phone-group">
                  <v-select
                      :options="sortedCountryList"
                      id="phoneNumber"
                      v-model="contactInfo.phone.countryInfo"
                      class="country-code"
                      :getOptionLabel="country => '+ ' + country.dialCode"
                      :reduce="country => { return {dialCode: country.dialCode, code: country.code}}"
                      name="DialCode">
                  </v-select>
                  <input type="text" class="number" :name="$t('phoneNumber')" v-validate="'required|numeric'" v-model="contactInfo.phone.phoneNumber" />
                </div>
                <span class="invalid">{{ errors.first($t('phoneNumber')) }}</span>
              </div>

              <div class="form-group">
                <label for="cellPhoneNumber">{{ $t('cellPhoneNumber') }}</label>
                <div class="phone-group">
                  <v-select
                      :options="sortedCountryList"
                      id="cellPhoneNumber"
                      v-model="contactInfo.cell.countryInfo"
                      class="country-code"
                      :getOptionLabel="country => '+ ' + country.dialCode"
                      :reduce="country => { return {dialCode: country.dialCode, code: country.code}}"
                      name="DialCode">
                  </v-select>
                  <input type="text" class="number" :name="$t('cellPhoneNumber')" v-validate="'required|numeric'" v-model="contactInfo.cell.phoneNumber" />
                </div>
                <span class="invalid">{{ errors.first($t('cellPhoneNumber')) }}</span>
              </div>

              <div class="form-group">
                <label for="address1">{{ $t('address') }}</label>
                <input v-validate="'required'" id="address1" v-model="customer.address.address1" type="text" :name="$t('address')" />
                <span class="invalid">{{ errors.first($t('address')) }}</span>
              </div>

              <div class="form-group">
                <label for="address2">{{ $t('address') }} 2</label>
                <input v-model="customer.address.address2" id="address2" type="text" name="Address 2" />
              </div>

              <div class="form-group">
                <label for="city">{{ $t('city') }}</label>
                <input v-validate="'required'" id="city" v-model="customer.address.cityName" type="text" :name="$t('city')" />
                <span class="invalid">{{ errors.first($t('city')) }}</span>
              </div>

              <div class="form-group">
                <label for="country">{{ $t('country') }}</label>
                <v-select v-validate="'required'" id="country" v-model="customer.address.country" :name="$t('country')" :options="getCountryList" :reduce="country => country.code" label="name">
                </v-select>
                <span class="invalid">{{ errors.first($t('country')) }}</span>
              </div>

              <div class="form-group">
                <label for="zipPostal">{{ $t('postalZipCode') }}</label>
                <input v-validate="'required'" id="zipPostal" v-model="customer.address.zipCode" type="text" class="input-half" :name="$t('postalZipCode')" />
                <span class="invalid">{{ errors.first($t('postalZipCode')) }}</span>
              </div>


            </div>

          </div>
        </div>
      </div>
      <div class="content-panel">
        <div class="content-panel">
          <div class="panel-title">
            <h3>{{ $t('paymentInformation') }}</h3>
          </div>
          <div class="panel-body">

            <div class="two-col-form">

              <div class="form-group">
                <label for="cardholderName">{{ $t('cardholderName') }}:</label>
                <input v-validate="'required'" id="cardholderName" v-model="cardInfo.cardHolder" type="text" :name="$t('cardholderName')" />
                <span class="invalid">{{ errors.first($t('cardholderName')) }}</span>
              </div>

              <div class="form-group">
                <label for="cardNumber">{{ $t('cardNumber') }}:</label>
                <div class="card-number-group">
                  <input v-validate="'required|credit_card'" id="cardNumber" v-model="cardInfo.cardNumber" type="text" :name="$t('cardNumber')" />
                  <img :src="getCardType(cardInfo.cardNumber, CARD_TYPES).icon" v-if="cardInfo.cardNumber.length > 5">
                </div>
                <span class="invalid">{{ errors.first($t('cardNumber')) }}</span>
              </div>

              <div class="form-group">
                <label for="cardExpiry">{{ $t('expiry') }}:</label>
                <div class="select-group">
                  <v-select 
                    v-validate="'required'" 
                    id="cardExpiry" 
                    v-model="cardInfo.expiryMonth" 
                    :name="$t('expiryMonth')" 
                    :options="getMonthList" 
                    :reduce="month => month.value" 
                    label="name">
                  </v-select>
                  <v-select 
                    v-validate="'required'" 
                    v-model="cardInfo.expiryYear" 
                    :name="$t('expiryYear')" 
                    :options="years" 
                    :reduce="year => year">
                  </v-select>
                  <span class="invalid">{{ errors.first($t('expiryMonth')) }}</span>
                  <span class="invalid">{{ errors.first($t('expiryYear')) }}</span>
                </div>
              </div>

              <div class="form-group">
                <label for="cardSecurity">{{ $t('cvv') }}:</label>
                <input v-validate="'required'" id="cardSecurity" v-model="cardInfo.cardCvv" class="input-half" type="text" :name="$t('cvv')" />
                <span class="invalid">{{ errors.first($t('cvv')) }}</span>
              </div>

            </div>

          </div>
        </div>
      </div>

      <div class="accept-terms-check">
        <input type="checkbox" id="acceptTermsCheck" v-model="acceptTermsCheck" />

        <label for="acceptTermsCheck">
          {{ $t('acceptFlightTermsParagraph')}} 
          <a href="/terms" :title="$t('viewTermsAndConditions')" target="_blank">{{ $t('viewTermsAndConditions') }}</a>
        </label>
      </div>

      <div class="book-cta">
          <a class="back" @click="$emit('previousStep')"><i class="fal fa-caret-left"></i> {{ $t('goBack') }}</a>
          <button class="btn btn-lg" v-if="!buttonLoader && acceptTermsCheck" @click="submitBooking()">
            <span>{{ $t('bookNow') }}</span>
          </button>
          <button disabled="disabled" class="btn btn-sec btn-lg disabled" v-else-if="!acceptTermsCheck">
            <span>{{ $t('bookNow') }}</span>
          </button>
      </div>
    </section>
</template>

<script>
import { mapGetters } from 'vuex'
export default {
  name: 'FlightBookingPayment',

  data() {
    return {
      iframe: {},
      acceptTermsCheck: true,
      buttonLoader: false,
      CARD_TYPES: [
        {regEx: /^4[0-9]{5}/ig, name: 'visa', icon: 'https://raw.githubusercontent.com/fracto-team/vue-credit-card/master/src/assets/visa_icon.png'},
        {regEx: /^5[1-5][0-9]{4}/ig, name: 'mastercard', icon: 'https://raw.githubusercontent.com/fracto-team/vue-credit-card/master/src/assets/master_icon.png'},
        {regEx: /^3[47][0-9]{3}/ig, name: 'amex', icon: 'https://raw.githubusercontent.com/fracto-team/vue-credit-card/master/src/assets/amex_icon.png'},
        {regEx: /^(5[06-8]\d{4}|6\d{5})/ig, name: 'maestro', icon: 'https://raw.githubusercontent.com/fracto-team/vue-credit-card/master/src/assets/maestro_icon.png'},
        {regEx: /^979/ig, name: 'troy', icon: 'https://raw.githubusercontent.com/fracto-team/vue-credit-card/master/src/assets/troy_icon.png'},
      ],
      cardInfo: {
        expiryMonth: '',
        expiryYear: '',
        cardHolder: '',
        cardNumber: '',
        cardCvv: '',
      },
      customer: {
        firstName: '',
        lastName: '',
        email: '',
        nationality: '',
        phone: '',
        cellPhone: {},
        address: {
          address1: '',
          address2: '',
          zipCode: '',
          cityName: '',
          country: '',
        }
      },
      contactInfo: {
        phone: {
          countryInfo: {},
          phoneNumber: ''
        },
        cell: {
          countryInfo: {},
          phoneNumber: null
        }
      }
    }
  },

  mounted() {
    this.currentYear = new Date().getYear() + 1900;
    let traveler = this.bookingSummary.travelers[0];
    this.customer.firstName = traveler.firstName;
    this.customer.lastName = traveler.lastName;
    this.customer.email = traveler.email;
    this.contactInfo.phone.countryInfo = {dialCode: traveler.phoneNumber.countryCallingCode, code: traveler.phoneNumber.country};
    this.contactInfo.phone.phoneNumber = traveler.phoneNumber.numberWithoutCountryCode;
    this.contactInfo.cell.countryInfo = {dialCode: traveler.phoneNumber.countryCallingCode, code: traveler.phoneNumber.country};
    this.contactInfo.cell.phoneNumber = traveler.phoneNumber.numberWithoutCountryCode;
  },

  props: ['bookingSummary', 'currency'],

  methods: {
    submitBooking() {
      //this.$emit('book', this.iframe);
      let self = this;
      self.$validator.validateAll().then((result) => {
        if (result) {
          self.customer.phone = `+${self.contactInfo.phone.countryInfo.dialCode}${self.contactInfo.phone.phoneNumber}`;
          self.customer.cellPhone = {
            country: self.contactInfo.cell.countryInfo.code,
            countryCallingCode: self.contactInfo.cell.countryInfo.dialCode,
            numberWithoutCountryCode: self.contactInfo.cell.phoneNumber
          };
          this.$emit('book', {customer: self.customer, cardInfo: self.cardInfo});
        }
      });
    },
    getCardType(cardNum, cardTypes = []) {
      if (!cardTypes) {
        cardTypes = this.CARD_TYPES;
      }
      let cardType = null;
      for (let j = 0; j < cardTypes.length; j++) {
        if (cardNum.match(cardTypes[j].regEx)) {
          cardType = cardTypes[j];
          break;
        }
      }
      return cardType;
    },
    removeDuplicates(myArr, prop) {
      return myArr.filter((obj, pos, arr) => {
        return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
      });
    }
  },
  computed: {
    ...mapGetters([
      'getMonthList',
      'getCountryList'
    ]),
    sortedCountryList() {
      let dialCodesArr = this.removeDuplicates(this.getCountryList, "dialCode");

      let sortedList = dialCodesArr.sort((a, b) => (a.dialCode > b.dialCode) ? 1 : -1);
      return sortedList
    },
    years() {
      let currentYear = new Date().getYear() + 1900;
      return new Array(20).fill(currentYear).map((n,i) => n + i)
    }
  }
}
</script>